import React, { useEffect, useRef, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import useFocusTrap from "../../../hooks/useFocusTrap";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";
import { resolvedOrderKind, accountKindLabel, accountKindReasonText } from "../../../utils/orderAccountKind";
import { isFlagStateUnknown } from "../../../utils/flagState";
import {
  describeRefusal,
  isConsoleMismatch,
  consoleMismatchText,
} from "../../Payments/WireReconciliation/wireReceiptUi";

const MIN_NOTES = 10;

/**
 * N31 (QA 2026-08-07). The mark-paid refusal returns a machine token in
 * `message` ('settlement_refused') and the real reason in `code`. This screen
 * rendered `message` only, so a refused AD saw a red banner reading literally
 * "settlement_refused" — no reason, no next step, and no way to tell "this order
 * is cancelled" from "this order is in the wrong currency". The sibling wire
 * screen already branches on `data.code` (WireReconciliation.jsx:215).
 *
 * Keyed on the settlement ledger's own refusal reasons; keep in step with
 * app/services/settlementLedger.service.js.
 *
 * R8 (2026-10-07): ONE map, shared with the wire queue
 * (wireReceiptUi.describeRefusal), so a WIRE order refused here and the same
 * refusal in the wire queue read the same. It is keyed on `code`, or on `error`
 * for the role gate's 403 (requireRole answers { error: 'role_forbidden' } with
 * no message), and reads the extras the API puts under `data` (the order that
 * already holds a bank reference, the active receipt, why an amount was refused).
 */

/**
 * A refusal in words, or null when the error is not a coded refusal. A5: the
 * console's 503 FLAG_STATE_UNKNOWN is coded under data.reason too.
 */
function refusalText(data) {
  const code = data && (data.code || data.error || isFlagStateUnknown(data));
  if (!code) return null;
  // An unrecognised code is still better named than swallowed.
  return describeRefusal(data, "record").text;
}

/**
 * N34 (QA 2026-08-07). The currency health of a row, from the campaign's own
 * currency rather than the order's. Observed on dev: orders against campaign
 * 2505 (currency_type NULL) rendered "10000 CAD" — indistinguishable from a
 * healthy row, on the one screen where a human could have caught it.
 */
function currencyState(r) {
  if (r.currencyUndeclared) return "undeclared";
  if (r.currencyMismatch) return "mismatch";
  return "ok";
}

/**
 * I8 (corporate Business PAD contract, 2026-10-25): a company order settles
 * only when the money came from the company's own account, and
 * recordSettlement refuses every manual credit of an ENTITY order
 * (REMITTER_NOT_CONFIRMED) until the remitter check is MATCH. Before such a row
 * is confirmed here, the AD compares the remitter on the statement with the
 * expected one (the legal entity name for an ENTITY order, the person for an
 * INDIVIDUAL one) and the name the investor declared, and records MATCH or
 * MISMATCH (a note is required for a mismatch) through
 * POST admin/investments/:id/wire-remitter-check.
 *
 * WHICH ROWS (Amendment A3/A5, closes R9): the server says, in each row's
 * remitterCheckRequired (api common/remitterCheck.js: every unsettled ENTITY
 * order on any rail, and every unsettled WIRE order; exactly the orders the
 * remitter-check endpoint accepts). The page used to decide it from
 * payment_method and exempted ACH, so an ENTITY ACH row was confirmed with no
 * check and refused by the ledger. See remitterRequirementOf.
 *   - A row whose step is required cannot be confirmed while its check is
 *     UNCHECKED, while a selected result is not recorded yet, or while a save
 *     is in flight.
 *   - A MISMATCH holds a company order, and an order whose kind is not
 *     resolved (fail closed): it cannot be confirmed here.
 *   - A MISMATCH on an INDIVIDUAL order is shown; the server decides.
 *   - A MATCH is recorded, and the payment confirmed, only when the order's
 *     kind is resolved and the server's expectedRemitter.kind is that kind (and
 *     names someone). An order of unresolved kind cannot be confirmed here.
 *   - Replacing a recorded MISMATCH with a MATCH needs an explicit
 *     confirmation and a reason in the note.
 *   - A row the server says needs no check has no remitter step, but it is
 *     not exempt from the kind: recordSettlement refuses an order of
 *     unresolved kind on any rail (Amendment A1, ACCOUNT_KIND_UNRESOLVED), so
 *     such a row of unresolved kind is held too.
 *   - A row whose remitterCheckRequired is missing or not a boolean cannot be
 *     confirmed here (fail closed, as for ACCOUNT_KIND_NOT_SENT).
 * The list's remitter fields (wire_remitter_name / _check / _note, accountKind,
 * expectedRemitter) are read when the API sends them; an absent check reads as
 * UNCHECKED, so a missing field never lets a payment through unchecked, and an
 * absent expectedRemitter means a MATCH cannot be recorded (nothing to match).
 */
const REMITTER_LABELS = { UNCHECKED: "Not checked", MATCH: "Match", MISMATCH: "Mismatch" };
const REMITTER_NOTE_MAX = 500;

/**
 * Whether the remitter step applies to a pending row, as the server said:
 * 'REQUIRED', 'NOT_REQUIRED' or 'UNKNOWN'. It reads one field of the row and
 * no other: remitterCheckRequired, and only a real boolean counts. Anything
 * else (absent, null, "true", 1) is UNKNOWN, and an UNKNOWN row cannot be
 * confirmed here.
 *
 * Neither payment_method nor paymentOption is read for it (Amendment A3/A5).
 * The rail alone does not say it: an ENTITY order needs the check on any
 * rail, and the AD console's manual credit of an ACH order settles on the
 * manual-bank rail. paymentOption is investor-writable and never decided
 * anything here (review round 3, 2026-10-25).
 */
export function remitterRequirementOf(r) {
  if (!r || typeof r !== "object") return "UNKNOWN";
  if (r.remitterCheckRequired === true) return "REQUIRED";
  if (r.remitterCheckRequired === false) return "NOT_REQUIRED";
  return "UNKNOWN";
}

const remitterStepShown = (r) => remitterRequirementOf(r) === "REQUIRED";

const REMITTER_REQUIREMENT_UNKNOWN_HOLD =
  "This payment cannot be confirmed here because the server did not say whether a sender check is needed. Refresh the page.";

/** The list's remitter cell: [label, data-remitter-check]. */
function remitterCell(r) {
  const requirement = remitterRequirementOf(r);
  if (requirement === "REQUIRED") return [REMITTER_LABELS[remitterCheckOf(r)], remitterCheckOf(r)];
  if (requirement === "NOT_REQUIRED") return ["Not required", "NOT_REQUIRED"];
  return ["Not stated by the server", "NOT_SENT"];
}

function remitterCheckOf(r) {
  const v = String((r && (r.wire_remitter_check || r.wireRemitterCheck)) || "").toUpperCase();
  return v === "MATCH" || v === "MISMATCH" ? v : "UNCHECKED";
}

const declaredRemitterOf = (r) => (r && (r.wire_remitter_name || r.wireRemitterName)) || null;

/**
 * The name the wire must come from, as the server sent it, or null. A MATCH
 * is a comparison with this name, so without it a MATCH cannot be recorded
 * (fail closed); a MISMATCH still can.
 */
function expectedRemitterName(r) {
  const name = r && r.expectedRemitter && r.expectedRemitter.name;
  return typeof name === "string" && name.trim() ? name.trim() : null;
}
const remitterNoteOf = (r) => (r && (r.wire_remitter_note || r.wireRemitterNote)) || null;

/**
 * Why the server's expected remitter cannot back a MATCH for this order, or
 * null. A MATCH says the wire came from the account holder the order needs:
 * the order's resolved kind (resolvedOrderKind) must be known, and the
 * server's expectedRemitter must name someone of that same kind
 * (expectedRemitter.kind). Recording a MATCH and confirming the wire both need
 * this (fail closed); a MISMATCH stays recordable.
 *   -> 'KIND_UNRESOLVED' | 'NO_EXPECTED' | 'KIND_MISMATCH' | null
 */
function expectedRemitterProblem(r) {
  const kind = resolvedOrderKind(r).kind;
  if (!kind) return "KIND_UNRESOLVED";
  if (!expectedRemitterName(r)) return "NO_EXPECTED";
  if (r.expectedRemitter.kind !== kind) return "KIND_MISMATCH";
  return null;
}

function matchBlockText(r) {
  switch (expectedRemitterProblem(r)) {
    case "KIND_UNRESOLVED":
      return `This order's account kind is not resolved (${accountKindReasonText(resolvedOrderKind(r).reason)}), so a match cannot be recorded here. A mismatch can still be recorded.`;
    case "NO_EXPECTED":
      return "The server sent no expected remitter for this order, so a match cannot be recorded here. A mismatch can still be recorded.";
    case "KIND_MISMATCH":
      return "The server's expected remitter does not name this order's account kind, so a match cannot be recorded here. A mismatch can still be recorded.";
    default:
      return null;
  }
}

function confirmBlockText(r) {
  switch (expectedRemitterProblem(r)) {
    case "KIND_UNRESOLVED":
      return `This order's account kind is not resolved (${accountKindReasonText(resolvedOrderKind(r).reason)}), so this payment cannot be confirmed here.`;
    case "NO_EXPECTED":
      return "The server sent no expected remitter for this order, so this payment cannot be confirmed here.";
    case "KIND_MISMATCH":
      return "The server's expected remitter does not name this order's account kind, so this payment cannot be confirmed here.";
    default:
      return null;
  }
}

/**
 * Why this row cannot be confirmed yet, or null. A row the server did not say
 * a remitter step for: always held. A row the server says needs none: held
 * only while its kind is not resolved. A row whose step is required is held on
 * a company (or unresolved) MISMATCH first, then while its kind or its
 * expected remitter cannot back a MATCH, then while its check is UNCHECKED.
 */
function remitterHold(r) {
  if (!r) return null;
  const requirement = remitterRequirementOf(r);
  if (requirement === "UNKNOWN") return REMITTER_REQUIREMENT_UNKNOWN_HOLD;
  if (requirement === "NOT_REQUIRED") {
    const resolved = resolvedOrderKind(r);
    return resolved.kind
      ? null
      : `This order's account kind is not resolved (${accountKindReasonText(resolved.reason)}), so this payment cannot be confirmed here.`;
  }
  const check = remitterCheckOf(r);
  if (check === "MISMATCH" && resolvedOrderKind(r).kind !== "INDIVIDUAL") {
    return "The remitter does not match. A company order's payment must come from the company's own account, so this payment is held: do not confirm it here, escalate to compliance.";
  }
  const blocked = confirmBlockText(r);
  if (blocked) return blocked;
  if (check === "UNCHECKED") return "Record the remitter check before confirming this payment.";
  return null;
}

/**
 * A remitter-check refusal in words. A5: the endpoint's own codes are worded
 * here, never with the server's text. NOT_WIRE_ROUTED no longer comes from it
 * (it accepts every unsettled ENTITY order on any rail, and every WIRE order).
 */
const REMITTER_REFUSALS = {
  REMITTER_CHECK_NOT_APPLICABLE:
    "The sender check does not apply to this order: it is settled or closed, or it is neither a company order nor a wire. Nothing was recorded. Reload the queue to see the order as it is now.",
  EXPECTED_REMITTER_UNKNOWN:
    "The company name on file is missing, so the sender cannot be matched. Complete the company details first. Nothing was recorded.",
};

function remitterRefusalText(err) {
  const data = err?.response?.data || {};
  const code = data.code || data.error;
  if (err?.response?.status === 403 || code === "role_forbidden" || code === "no_role_assigned") {
    return "Only the AD can record a remitter check. Nothing was recorded.";
  }
  if (code && Object.prototype.hasOwnProperty.call(REMITTER_REFUSALS, code)) return REMITTER_REFUSALS[code];
  const message = typeof data.message === "string" && data.message.trim() ? data.message.trim() : null;
  if (message) return `Refused: ${message}. Nothing was recorded.`;
  if (code) return `Refused (${code}). Nothing was recorded.`;
  return "The remitter check could not be recorded. Reload the queue to see what was saved.";
}

const dangerTextStyle = { color: "var(--neg-700)", fontWeight: 600 };

const backdropStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  zIndex: 1000, display: "grid", placeItems: "center", padding: 20,
};
const dialogStyle = {
  background: "var(--bg-card)", border: "1px solid var(--bd)",
  borderRadius: "var(--r-md)", padding: 20, maxWidth: 560, width: "100%",
  maxHeight: "90vh", overflow: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
};

function ManualPaymentConfirm() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const [open, setOpen] = useState(null);
  const [grossFee, setGrossFee] = useState("");
  const [bankRef, setBankRef] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [receivedCurrency, setReceivedCurrency] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // I8: the remitter check being recorded in the open dialog.
  const [remitterResult, setRemitterResult] = useState("");
  const [remitterNote, setRemitterNote] = useState("");
  const [remitterBusy, setRemitterBusy] = useState(false);
  const [remitterError, setRemitterError] = useState(null);
  const [remitterOverride, setRemitterOverride] = useState(false);
  // The order whose dialog is open now (read by a remitter answer that lands late).
  const openIdRef = useRef(null);
  openIdRef.current = open ? open.id : null;
  // A remitter answer that lands after the page is left is dropped unread
  // (R23, review 2026-10-25): openIdRef keeps its last value after unmount,
  // so stillOpen() alone let a late answer set the row, the dialog and the
  // busy flag on an unmounted page. The cleanup invalidates it. The list
  // read and the mark-paid answer check it too.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const dialogRef = useFocusTrap({
    active: !!open,
    onClose: () => !submitting && !remitterBusy && setOpen(null),
  });

  function fetchList() {
    setLoading(true);
    // A list answer that lands after the page is left is dropped unread
    // (R23's sibling, review 2026-10-25).
    bdcApi
      .adListPendingPayments({ page, limit })
      .then((res) => {
        if (!mountedRef.current) return;
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  function openDialog(row) {
    // I8: no switching orders while a remitter check is being recorded.
    if (remitterBusy) return;
    setOpen(row);
    setGrossFee("");
    setBankRef("");
    setReceivedAmount("");
    setReceivedCurrency("");
    setNotes("");
    setRemitterResult("");
    setRemitterNote("");
    setRemitterError(null);
    setRemitterOverride(false);
  }

  function chooseRemitterResult(result) {
    setRemitterResult(result);
    setRemitterOverride(false);
  }

  function closeDialog() {
    if (submitting || remitterBusy) return;
    setOpen(null);
  }

  // Replacing a recorded MISMATCH with a MATCH lifts a hold: it needs an
  // explicit confirmation and a reason in the note, never one click.
  const overridingMismatch = !!open && remitterResult === "MATCH" && remitterCheckOf(open) === "MISMATCH";
  const remitterNoteMissing = (remitterResult === "MISMATCH" || overridingMismatch) && !remitterNote.trim();
  // A MATCH needs the order's resolved kind and an expected remitter of that kind.
  const matchBlock = open ? matchBlockText(open) : null;
  const matchUnavailable = !!matchBlock;
  const remitterBlocked =
    remitterNoteMissing ||
    (remitterResult === "MATCH" && matchUnavailable) ||
    (overridingMismatch && !remitterOverride);
  // I8: the hold reads what the server has. A selected result that is not
  // recorded yet, or a save still in flight, holds the confirmation too:
  // otherwise a MISMATCH on its way could be overtaken by mark-paid.
  const remitterPending = !open
    ? null
    : remitterBusy
    ? "The remitter check is being recorded. Wait for it before confirming."
    : remitterResult && remitterResult !== remitterCheckOf(open)
    ? "The remitter check you selected is not recorded yet. Record it, or select the recorded result again, before confirming."
    : null;
  const confirmHold = remitterHold(open) || remitterPending;

  async function recordRemitter() {
    if (!open || !remitterResult || remitterBlocked || remitterBusy) return;
    // The answer is bound to the order it was sent for: it patches that
    // order's row, and touches the dialog only while that order's is open.
    const id = open.id;
    const result = remitterResult;
    const stillOpen = () => openIdRef.current === id;
    const note = remitterNote.trim();
    const body = note ? { result, note } : { result };
    setRemitterBusy(true);
    setRemitterError(null);
    try {
      await bdcApi.adRecordWireRemitterCheck(id, body);
      if (!mountedRef.current) return;
      const patch = { wire_remitter_check: result, wire_remitter_note: note || null };
      setOpen((o) => (o && o.id === id ? { ...o, ...patch } : o));
      setRows((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      if (stillOpen()) {
        setRemitterResult("");
        setRemitterNote("");
        setRemitterOverride(false);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (stillOpen()) setRemitterError(remitterRefusalText(err));
    } finally {
      if (mountedRef.current) setRemitterBusy(false);
    }
  }

  async function submit(ev) {
    ev.preventDefault();
    // I8: never confirm a payment whose remitter is unchecked, a held
    // mismatch, one whose check is selected but not recorded yet, or a row the
    // server did not say a remitter step for.
    if (confirmHold) return;
    const grossNum = Number(grossFee);
    if (!Number.isFinite(grossNum) || grossNum < 0) {
      setFlash({ type: "danger", message: "Gross fee must be a non-negative number." });
      return;
    }
    if (notes.trim().length < MIN_NOTES) {
      setFlash({ type: "danger", message: `Notes must be at least ${MIN_NOTES} characters.` });
      return;
    }
    setSubmitting(true);
    try {
      const body = { gross_fee: grossNum, notes: notes.trim() };
      if (bankRef.trim()) body.bank_reference = bankRef.trim();
      // R8 / W1-M: what the bank credited. Required by the API for EVERY order
      // (the receipt rules settle only an exact amount in the payable currency).
      if (receivedAmount.trim()) body.received_amount = receivedAmount.trim();
      if (receivedCurrency) body.received_currency = receivedCurrency;
      const res = await bdcApi.adMarkInvestmentPaid(open.id, body);
      // A late answer on a page that is gone is dropped unread, and the list
      // is not read again from it (R23's sibling, review 2026-10-25).
      if (!mountedRef.current) return;
      if (isConsoleMismatch(res)) {
        // Recorded as an unresolved wire receipt: NOT paid, nothing locked.
        // Ordered vs credited, from the API's own figures.
        setFlash({ type: "warn", message: consoleMismatchText(res, open) });
        setOpen(null);
        fetchList();
        return;
      }
      const lines = res?.data?.commission_lines ?? 0;
      setFlash({
        type: "info",
        message: `Investment #${open.id} marked PAID. ${lines} commission line${lines === 1 ? "" : "s"} locked.`,
      });
      setOpen(null);
      fetchList();
    } catch (err) {
      if (!mountedRef.current) return;
      // N31: prefer the coded refusal reason over the server's machine token.
      const data = err?.response?.data;
      setFlash({
        type: "danger",
        message: refusalText(data) || data?.message || err.message || "Mark-paid failed",
      });
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="Manual payment confirmation"
        subtitle={<><span className="mono">{total}</span><span>pending · page {page} of {Math.max(totalPages, 1)}</span></>}
      />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        v1 investors wire funds to Roots' operating bank account. Reconcile the statement, then confirm each investment below with the amount, currency and reference the bank credited. Only an exact credit marks the order paid; any other credit is recorded as an unresolved mismatch. An order the investor never confirmed cannot be marked paid here. Confirmation writes the payment, locks attribution, and triggers commission-ledger rows in one transaction.
      </p>

      {flash && <Alert tone={flash.type === "ok" ? "info" : flash.type}>{flash.message}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No investments awaiting payment confirmation.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Inv #</th>
                  <th>User id</th>
                  <th>Campaign</th>
                  <th className="r">Amount</th>
                  <th>Currency</th>
                  <th>Suitability</th>
                  <th>Payment</th>
                  <th>Remitter</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-currency-state={currencyState(r)}>
                    <td className="mono">#{r.id}</td>
                    <td className="mono">#{r.user_id}</td>
                    <td className="mono">#{r.campaign_id}</td>
                    <td className="r">{r.amount}</td>
                    {/* N34: the CAMPAIGN's currency, not just the order's. An
                        undenominated campaign can no longer masquerade as CAD. */}
                    <td className="muted">
                      {r.currencyUndeclared ? (
                        <span style={dangerTextStyle}>No campaign currency</span>
                      ) : r.currencyMismatch ? (
                        <span style={dangerTextStyle}>
                          {r.currency || "—"} ≠ {r.payableCurrency}
                        </span>
                      ) : (
                        r.currency || "—"
                      )}
                    </td>
                    <td className="muted">{r.suitability_status || "—"}</td>
                    <td className="muted">{r.paymentStatus || "UNPAID"}</td>
                    <td className="muted" data-remitter-check={remitterCell(r)[1]}>
                      {remitterCell(r)[0]}
                    </td>
                    <td className="muted">{formatAuditTimestamp(r.createdAt)}</td>
                    <td className="r">
                      <Btn variant="primary" size="sm" onClick={() => openDialog(r)} disabled={remitterBusy}>Confirm paid</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1 || submitting} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>{page} / {totalPages}</span>
          <Btn size="sm" disabled={page === totalPages || submitting} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}

      {open && (
        <div
          style={backdropStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-pay-dialog-title"
          ref={dialogRef}
        >
          <form onSubmit={submit} style={dialogStyle}>
            <h3 id="ad-pay-dialog-title" style={{ fontSize: "var(--t-xl)", fontWeight: 600, margin: "0 0 12px" }}>
              Confirm payment for investment #{open.id}
            </h3>
            {/* N34: warn where the decision is actually made, not only in the
                list. Settlement will REFUSE this order (CURRENCY_UNDECLARED),
                so say so before the AD fills in a fee and a justification. */}
            {open.currencyUndeclared && (
              <Alert tone="danger">
                Campaign #{open.campaign_id} has <strong>no declared currency</strong>, so this
                payment cannot be settled against it. An administrator must set the campaign
                currency first — confirming here will be refused.
              </Alert>
            )}
            {open.currencyMismatch && (
              <Alert tone="danger">
                This order is denominated <strong>{open.currency}</strong> but campaign #{open.campaign_id}
                {" "}is payable in <strong>{open.payableCurrency}</strong>. Settlement will be refused —
                escalate rather than retrying.
              </Alert>
            )}
            <dl style={{ margin: "0 0 16px" }}>
              <KV label="Investor" value={`#${open.user_id}`} />
              <KV label="Campaign" value={`#${open.campaign_id}`} />
              <KV label="Amount" value={`${open.amount} ${open.currency || ""}`} />
              <KV
                label="Campaign currency"
                value={open.currencyUndeclared
                  ? "not declared — cannot be settled"
                  : (open.campaignCurrency || "—")}
              />
              <KV label="Suitability" value={open.suitability_status || "—"} />
              <KV label="Current payment status" value={open.paymentStatus || "UNPAID"} />
            </dl>

            {remitterStepShown(open) && (
              <section aria-labelledby="ad-pay-remitter-title" style={{ margin: "0 0 16px" }}>
                <h4 id="ad-pay-remitter-title" style={{ fontSize: "var(--t-md)", fontWeight: 600, margin: "0 0 4px" }}>
                  Who sent the payment
                </h4>
                <dl style={{ margin: "0 0 8px" }}>
                  <KV label="Account kind" value={accountKindLabel(resolvedOrderKind(open).kind)} />
                  <KV
                    label="Expected remitter"
                    value={expectedRemitterName(open) || "Not provided by the server"}
                  />
                  <KV label="Remitter declared by the investor" value={declaredRemitterOf(open) || "Not declared"} />
                  <KV
                    label="Remitter check"
                    value={`${REMITTER_LABELS[remitterCheckOf(open)]}${remitterNoteOf(open) ? ` (${remitterNoteOf(open)})` : ""}`}
                  />
                </dl>
                <fieldset style={{ border: 0, padding: 0, margin: 0 }} disabled={remitterBusy || submitting}>
                  <legend className="field__label">Result of the check</legend>
                  {matchBlock && <p className="field__hint">{matchBlock}</p>}
                  <label style={{ display: "block" }}>
                    <input
                      type="radio"
                      name="ad-pay-remitter-result"
                      value="MATCH"
                      checked={remitterResult === "MATCH"}
                      onChange={() => chooseRemitterResult("MATCH")}
                      disabled={matchUnavailable}
                    />{" "}
                    Match: the payment came from the expected account holder
                  </label>
                  <label style={{ display: "block" }}>
                    <input
                      type="radio"
                      name="ad-pay-remitter-result"
                      value="MISMATCH"
                      checked={remitterResult === "MISMATCH"}
                      onChange={() => chooseRemitterResult("MISMATCH")}
                    />{" "}
                    Mismatch: the payment came from another account holder
                  </label>
                  {overridingMismatch && (
                    <label style={{ display: "block", marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={remitterOverride}
                        onChange={(e) => setRemitterOverride(e.target.checked)}
                      />{" "}
                      I confirm that this match replaces the recorded mismatch: the payment came from the expected account holder after all. The reason is in the note.
                    </label>
                  )}
                  <div className="field" style={{ marginTop: 8 }}>
                    <label className="field__label" htmlFor="ad-pay-remitter-note">
                      Remitter note (required for a mismatch, and to replace a recorded mismatch)
                    </label>
                    <textarea
                      id="ad-pay-remitter-note"
                      className="field__textarea"
                      rows={2}
                      maxLength={REMITTER_NOTE_MAX}
                      value={remitterNote}
                      onChange={(e) => setRemitterNote(e.target.value)}
                    />
                    {remitterNoteMissing && (
                      <span className="field__hint">
                        {overridingMismatch
                          ? "A reason is required to replace a recorded mismatch with a match."
                          : "A note is required for a mismatch."}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Btn
                      type="button"
                      onClick={recordRemitter}
                      disabled={!remitterResult || remitterBlocked || remitterBusy || submitting}
                    >
                      Record remitter check
                    </Btn>
                  </div>
                </fieldset>
                {remitterError && <p style={dangerTextStyle}>{remitterError}</p>}
              </section>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field
                label="Roots gross fee on this transaction (dollars)"
                name="gross_fee"
                type="number"
                step="0.01"
                min="0"
                value={grossFee}
                onChange={(e) => setGrossFee(e.target.value)}
                disabled={submitting}
                autoFocus
                required
              />

              <Field
                label="Bank / wire reference (required)"
                name="bank_reference"
                type="text"
                maxLength={255}
                placeholder="WT-2026-XXXX"
                value={bankRef}
                onChange={(e) => setBankRef(e.target.value)}
                disabled={submitting}
              />

              {/* R8 / D9, W1-M: an order settles only when the credit equals the
                  order exactly, in the currency it is payable in, whatever its
                  rail. Anything else is recorded as an unresolved mismatch and
                  the order stays unpaid.
                  No min and step="any": the browser must not refuse a zero,
                  negative or sub-cent figure on its own; the server decides
                  (400 DECLARED_AMOUNT_INVALID) and its refusal is shown. */}
              <Field
                label="Amount the bank credited (required)"
                name="received_amount"
                type="number"
                step="any"
                value={receivedAmount}
                onChange={(e) => setReceivedAmount(e.target.value)}
                disabled={submitting}
              />
              <div className="field">
                <label className="field__label" htmlFor="ad-pay-received-currency">
                  Currency the bank credited (required)
                </label>
                <select
                  id="ad-pay-received-currency"
                  className="field__input"
                  value={receivedCurrency}
                  onChange={(e) => setReceivedCurrency(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select a currency</option>
                  {Array.from(new Set(["CAD", "USD", open.payableCurrency, open.currency].filter(Boolean))).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <span className="field__hint">
                  Settles only on an exact match with {open.amount} {open.payableCurrency || open.currency || ""}; any other credit is recorded as an unresolved mismatch.
                </span>
              </div>

              <div className="field">
                <label className="field__label" htmlFor="ad-pay-notes">
                  Notes (required, min {MIN_NOTES} chars) — audited justification
                </label>
                <textarea
                  id="ad-pay-notes"
                  className="field__textarea"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={submitting}
                  required
                  minLength={MIN_NOTES}
                />
                <span className="field__hint">{notes.trim().length} / {MIN_NOTES}</span>
              </div>
            </div>

            {confirmHold && (
              <p style={{ ...dangerTextStyle, marginTop: 12 }} data-remitter-hold>
                {confirmHold}
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Btn type="button" onClick={closeDialog} disabled={submitting || remitterBusy}>Cancel</Btn>
              <Btn
                variant="primary"
                type="submit"
                disabled={submitting || notes.trim().length < MIN_NOTES || !!confirmHold}
              >
                {submitting ? "Confirming…" : "Confirm paid + lock commissions"}
              </Btn>
            </div>
          </form>
        </div>
      )}
    </PageShell>
  );
}

const KV = ({ label, value }) => (
  <>
    <dt className="field__label" style={{ marginTop: 8 }}>{label}</dt>
    <dd style={{ margin: "2px 0 0", fontSize: "var(--t-md)" }}>{value}</dd>
  </>
);

export default ManualPaymentConfirm;
